import { type ComputedRef, type WritableComputedRef, computed } from "vue";
import { useServiceStore } from "../../../store/service";

export interface FieldRef<T> {
  field: Record<string, unknown>;
  property: WritableComputedRef<T>;
}

export function usePropertyBuilder() {
  const service = useServiceStore();

  function buildConfig<T>(configName: string, defaultValue: T): WritableComputedRef<T> {
    return computed({
      get() {
        return service.ctx.profile?.publicConfigs[configName] ?? defaultValue;
      },
      set(value: T) {
        service.setConfig({ config: configName, value: value });
      },
    });
  }

  function buildProperty<T>(configName: string, variableName: string, default_value: T): WritableComputedRef<T> {
    return computed({
      get() {
        const config = service.ctx.profile?.publicConfigs[configName];
        if (config && config[variableName] !== undefined) {
          return config[variableName];
        } else {
          return default_value;
        }
      },
      set(value: T) {
        service.setConfField({ config: configName, field: variableName, value: value });
      },
    });
  }

  /**
   * Get a reference to both the config field definition for a field and its writable value
   * @param sectionName The name of the section to search in
   * @param configName The name of the config to search in
   * @param variableName The variable name to get the reference for
   */
  function buildField<T>(
    sectionName: string,
    configName: string,
    variableName: string,
  ): ComputedRef<FieldRef<T> | undefined> {
    return computed(() => {
      const field = service.ctx.profile?.configDefinition?.sections?.[sectionName]?.variables.find(
        (variable: Record<string, unknown>) => variable.config == configName && variable.name == variableName,
      );
      if (field) {
        return {
          field,
          property: buildProperty(configName, variableName, field.default),
        };
      }
      return undefined;
    });
  }

  return { buildConfig, buildProperty, buildField };
}
